import { useCallback, useEffect, useState } from "react";

/**
 * Public (unauthenticated, token-gated) leaderboard hooks. Loaded by
 * display-agent webviews via `/embed/leaderboard/:id?token=…`. The page
 * starts with the token from the URL query and renews it transparently
 * — reactive on 401, proactive when expiry is within 24 h.
 *
 * These hooks live separately from the auth'd cloud-ui API hooks because
 * they don't use `useApi()` (no JWT in localStorage), they don't use the
 * cloud's UI WebSocket (no session), and they own their own renewal
 * state machine.
 */

export interface PublicLeaderboardConfig {
  id: string;
  name: string;
  subheading: string | null;
  brandingTemplateId: string | null;
  expiresAt: string;
}

export interface PublicLeaderboardEntry {
  position: number;
  driverId: string;
  driverName: string;
  /** Raw `carId/skinId` string from the session. */
  car: string;
  /** Human label resolved by the cloud content mirror (e.g. "Porsche 911
   *  GT3 RS — Dark Blue"). Falls back to `car` if the mirror has no row. */
  carName?: string;
  track: string;
  trackName?: string;
  eventName: string;
  bestLapTimeMs: number;
  bestLapSector1Ms?: number;
  bestLapSector2Ms?: number;
  bestLapSector3Ms?: number;
}

/** Renew the token (idempotent on the row). Caller passes the current
 *  token; we return the successor on success (within grace), `null` on
 *  past-grace (410). Errors and other non-OK responses also return null
 *  so callers fall through to the "session expired" empty state. */
async function renewOnce(
  leaderboardId: string,
  currentToken: string,
): Promise<{ token: string; expiresAt: string } | null> {
  const res = await fetch(`/api/leaderboard/saved/${leaderboardId}/renew`, {
    method: "POST",
    headers: { Authorization: `Bearer ${currentToken}` },
  });
  if (res.status === 410) return null;
  if (!res.ok) return null;
  const json = (await res.json()) as { success: boolean; data?: { token: string; expiresAt: string } };
  if (!json.success || !json.data) return null;
  return json.data;
}

interface FetcherState {
  token: string;
  setToken: (t: string) => void;
  setExpired: (e: boolean) => void;
  leaderboardId: string;
}

/**
 * Bearer-fetch with reactive renewal. On a 401, fires `/renew` with the
 * current token; if it works, swaps to the successor and retries the
 * original request once. If renew returns 410 (past grace), flips the
 * `expired` flag on the page so the empty state renders.
 */
export function usePublicLeaderboardFetcher(state: FetcherState): (path: string) => Promise<unknown> {
  const { token, setToken, setExpired, leaderboardId } = state;
  return useCallback(
    async (path: string): Promise<unknown> => {
      let active = token;
      let res = await fetch(path, { headers: { Authorization: `Bearer ${active}` } });
      if (res.status === 401) {
        const next = await renewOnce(leaderboardId, active);
        if (!next) {
          setExpired(true);
          return null;
        }
        setToken(next.token);
        active = next.token;
        res = await fetch(path, { headers: { Authorization: `Bearer ${active}` } });
      }
      if (!res.ok) return null;
      const json = await res.json();
      if (!json?.success) return null;
      return json.data;
    },
    [token, setToken, setExpired, leaderboardId],
  );
}

/** Fetch the saved leaderboard's config once (on mount and on token swap).
 *  Includes `expiresAt` so the page can drive proactive renewal. */
export function usePublicLeaderboardConfig(
  leaderboardId: string,
  fetcher: (path: string) => Promise<unknown>,
): { data: PublicLeaderboardConfig | null } {
  const [data, setData] = useState<PublicLeaderboardConfig | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const c = (await fetcher(`/api/leaderboard/saved/${leaderboardId}/config`)) as
        | PublicLeaderboardConfig
        | null;
      if (!cancelled && c) setData(c);
    })();
    return () => { cancelled = true; };
  }, [fetcher, leaderboardId]);
  return { data };
}

/** Poll the ranked entries on the given interval. */
export function usePublicLeaderboardEntries(
  leaderboardId: string,
  fetcher: (path: string) => Promise<unknown>,
  pollMs: number,
): { data: PublicLeaderboardEntry[] } {
  const [data, setData] = useState<PublicLeaderboardEntry[]>([]);
  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const rows = (await fetcher(`/api/leaderboard/saved/${leaderboardId}/entries`)) as
        | PublicLeaderboardEntry[]
        | null;
      if (!cancelled && Array.isArray(rows)) setData(rows);
    };
    void tick();
    const handle = setInterval(() => void tick(), pollMs);
    return () => { cancelled = true; clearInterval(handle); };
  }, [fetcher, leaderboardId, pollMs]);
  return { data };
}

/** Fire a background renewal if the current token is within `windowMs`
 *  of its `expiresAt`. Cheap belt-and-braces so the SPA almost never
 *  hits the reactive 401 path. */
export function useProactiveRenewal(
  leaderboardId: string,
  expiresAt: string | undefined,
  token: string,
  setToken: (t: string) => void,
  windowMs = 24 * 60 * 60 * 1000,
): void {
  useEffect(() => {
    if (!expiresAt || !token) return;
    const msLeft = new Date(expiresAt).getTime() - Date.now();
    if (msLeft >= windowMs) return;
    void renewOnce(leaderboardId, token).then((next) => {
      if (next) setToken(next.token);
    });
  }, [leaderboardId, expiresAt, token, setToken, windowMs]);
}
